<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import NavBar from './components/NavBar/index.vue'
import { useAppStore } from './hooks/useAppStore'
import { useGlobalLoading } from './hooks/useGlobalLoading'
import { useToast } from './hooks/useToast'
import { useGlobalError } from './hooks/useGlobalError'
import { usePageVisibility } from './hooks/usePageVisibility'
import GlobalLoading from './components/GlobalLoading/index.vue'
import GlobalError from './components/GlobalError/index.vue'
import Toast from './components/Toast/index.vue'
import AppInitError from './components/AppInitError/index.vue'

const route = useRoute()
const { appState, initApp, cleanupApp } = useAppStore()
const { loadingState } = useGlobalLoading()
const { toastState } = useToast()
const { globalErrorState, hideGlobalError } = useGlobalError()

// 初始化页面可见性监听，自动处理后台切入前台的刷新逻辑
usePageVisibility()

// 直接读取路由的 meta.title
const pageTitle = computed(() => {
  return (
    (route.query?.title as string) ||
    (route.meta?.title as string) ||
    appState.config.title
  )
})

// 判断是否为首页或我的页面
const isMainPage = computed(() => {
  const path = route.path
  return path === '/' || path === '/profile'
})

onMounted(async () => {
  // 初始化全局应用状态（仅包含用户信息和基础配置）
  // 首页数据现在在 home.vue 组件内部管理
  await initApp()
})

onUnmounted(() => {
  // 清理应用
  cleanupApp()
})
</script>

<template>
  <div class="flex flex-col min-h-screen">
    <!-- App初始化错误页面 -->
    <AppInitError v-if="appState.hasInitError" />

    <GlobalLoading v-else-if="!appState.hasInit" />

    <template v-else>
      <!-- 全局 Loading 遮罩 -->
      <transition name="fade" appear>
        <GlobalLoading v-if="loadingState.global" />
      </transition>

      <!-- 全局错误提示 -->
      <GlobalError
        v-model:is-visible="globalErrorState.isVisible"
        :title="globalErrorState.title"
        :message="globalErrorState.message"
        @close="hideGlobalError"
      />

      <!-- 全局Toast提示 -->
      <Toast
        v-model:is-visible="toastState.isVisible"
        :type="toastState.type"
        :title="toastState.title"
        :message="toastState.message"
        :duration="toastState.duration"
        :show-close-button="toastState.showCloseButton"
      />

      <!-- 全局顶部导航栏 -->
      <NavBar :title="pageTitle" :is-main-page="isMainPage" />

      <!-- 主内容区域 -->
      <div class="flex-1">
        <router-view />
      </div>
    </template>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
